import React, { useMemo } from 'react';
import { IconButton } from '@material-ui/core';
import { ArrowBackIosSharp, ArrowForwardIosSharp } from '@material-ui/icons';
import { TablePaginationWrapper } from './table-pagination.styled';
import Select, { SelectOptionProps } from 'components/form/select';

type TTablePagination = {
    page: number;
    totalRows: number;
    rowsPerPage?: number;
    setPage: (page: number) => void;
    setLimit: (limit: number) => void;
    refetch?: () => void;
};

const paginationOptions: SelectOptionProps[] = [
    {
        key: '10',
        value: '10',
    },
    {
        key: '25',
        value: '25',
    },
    {
        key: '50',
        value: '50',
    },
    {
        key: '100',
        value: '100',
    },
    {
        key: '10000000',
        value: 'All',
    },
];

const TablePagination = ({
    page,
    totalRows,
    rowsPerPage = 50,
    setPage,
    setLimit,
    refetch,
}: TTablePagination) => {
    const handleRefetch = (): void => {
        refetch && refetch();
    };
    const handleBackButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        setPage(page - 1);
        handleRefetch();
    };

    const handleNextButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        setPage(page + 1);
        handleRefetch();
    };

    const handleRowsPerPageChange = (
        e: React.ChangeEvent<{ value: unknown }>,
    ) => {
        setLimit(parseInt(e.target.value as string, 10));
        handleRefetch();
    };

    const pageRange = useMemo(() => {
        if (rowsPerPage > 100000) {
            return `Total: ${totalRows}`;
        }

        const start = page * rowsPerPage + 1;
        const end =
            start + rowsPerPage - 1 > totalRows
                ? totalRows
                : start + rowsPerPage - 1;
        return `${start}-${end} of ${totalRows}`;
    }, [page, totalRows, rowsPerPage]);

    return (
        <TablePaginationWrapper>
            <div>
                <Select
                    label="Rows per page:"
                    labelPosition="row"
                    value={rowsPerPage.toString()}
                    onChange={handleRowsPerPageChange}
                    options={paginationOptions}
                />
            </div>
            <div>{pageRange}</div>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
                size="small"
            >
                <ArrowBackIosSharp fontSize="small" />
            </IconButton>

            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(totalRows / rowsPerPage) - 1}
                aria-label="next page"
                size="small"
            >
                <ArrowForwardIosSharp fontSize="small" />
            </IconButton>
        </TablePaginationWrapper>
    );
};

export default TablePagination;
